import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { promotionKeys } from "./query-keys";

/**
 * A SKU master row, normalised to the `{ sku, name }` shape the picker's
 * confirm/resolve plumbing already speaks (mirrors `ResolvedSku`). The BE row
 * carries `sino_sku` / `product_name`, which we map here so the rest of the
 * picker is agnostic to which surface (search vs resolve) produced the row.
 */
export type SkuListItem = components["schemas"]["ResolvedSku"];

/** How many SKU rows to fetch per search. */
const SEARCH_LIMIT = 50;

/**
 * Cross-domain READ of the stock module's SKU master catalogue
 * (`GET /v1/sku`) for the promotion picker's browse/search surface.
 *
 * Contract note: the endpoint filters by `name_contains` (a case-insensitive
 * substring match on `product_name`) — there is NO `search` param on this path
 * (a `search` query is silently ignored by the BE). With an empty/whitespace
 * term we omit the filter entirely so the picker can browse the full catalogue.
 *
 * Feature isolation: this calls the shared api client directly and never imports
 * the stock/inventory feature's hooks or components. Requires `stock:read`
 * (the admin role has it; a 403 would surface via the shared error middleware).
 */
export function useSearchSkus(term: string, enabled = true) {
  const trimmed = term.trim();

  return useQuery({
    queryKey: promotionKeys.skuSearch(trimmed),
    enabled,
    queryFn: async (): Promise<SkuListItem[]> => {
      const { data, error } = await apiClient.GET("/v1/sku", {
        params: {
          query: {
            limit: SEARCH_LIMIT,
            ...(trimmed ? { name_contains: trimmed } : {}),
          },
        },
      });
      /* v8 ignore next -- unreachable: client errorMiddleware throws ApiError on !ok */
      if (error) throw error;

      const seen = new Set<string>();
      const items: SkuListItem[] = [];
      for (const row of data?.items ?? []) {
        const sku = row.sino_sku;
        if (!sku || seen.has(sku)) continue;
        seen.add(sku);
        items.push({ sku, name: row.product_name ?? "" });
      }
      return items;
    },
    staleTime: 30_000,
  });
}
